<template>
  <div class="p-1 convocation-public">
    <div v-if="showError" class="w-100 text-center pt-4">
      <h2 class="mb-1">Page Not Found</h2>
      <p class="mb-2">This registration link is invalid.</p>
      <b-img fluid :src="imgUrl" alt="Error page" />
    </div>

    <b-row v-else-if="pageLoading" align-h="center">
      <b-col class="text-center" lg="9" md="11">
        <b-spinner
          class="m-5"
          style="width: 4rem; height: 4rem"
          variant="primary"
          label="Spinning"
        />
      </b-col>
    </b-row>

    <template v-else>
      <b-row v-if="submitted" align-h="center">
        <b-col class="text-center" lg="9" md="11">
          <b-card class="py-3">
            <feather-icon icon="CheckCircleIcon" size="48" class="text-success mb-1" />
            <h4>Registration submitted</h4>
            <p class="text-muted mb-2">Thank you. Your convocation registration has been received.</p>
            <b-button variant="primary" @click="resetForAnother">Add another</b-button>
          </b-card>
        </b-col>
      </b-row>

      <template v-else>
        <!-- School header (inquiry public pattern) -->
        <b-row align-h="center">
          <b-col class="text-center mb-1" lg="9" md="11">
            <b-img
              v-if="school.logo"
              :src="school.logo"
              style="object-fit: contain"
              fluid
              height="120px"
              width="120px"
            />
            <b-img
              v-else
              src="https://cdn.cloudious.net/file-1776666463641-10693955.png"
              style="object-fit: contain"
              fluid
              height="120px"
              width="120px"
            />
            <div class="mt-50">
              <h3>{{ school.name || "Convocation Registration" }}</h3>
              <h5 v-if="school.address">
                <feather-icon icon="MapPinIcon" class="mr-50" />
                {{ school.address }}
              </h5>
              <h5 v-if="school.phone">
                <feather-icon icon="PhoneIcon" class="mr-50" />
                {{ school.phone }}
              </h5>
            </div>
          </b-col>
        </b-row>

        <b-row align-h="center">
          <b-col lg="10" md="11">
            <b-card class="convocation-form-card">
              <h4 class="text-uppercase mb-0">Convocation Registration Form</h4>
              <hr class="section-divider" />

              <b-form @submit.prevent="submitForm">
                <!-- Circular photo upload -->
                <b-row class="photo-row mb-2">
                  <b-col cols="12" class="d-flex justify-content-center">
                    <b-form-group label=" " label-class="d-none">
                      <b-media no-body class="justify-content-center">
                        <div
                          v-if="!photoPreview && !photoUploading"
                          class="photo-circle photo-circle--empty"
                          @click="triggerPhotoInput"
                        >
                          <b-img
                            src="https://cdn.cloudious.net/file-1677912450367-333289208.png"
                            rounded="circle"
                            height="120"
                            width="120"
                          />
                        </div>
                        <div
                          v-else-if="photoUploading"
                          class="photo-circle photo-circle--empty"
                        >
                          <b-spinner variant="primary" type="grow" />
                        </div>
                        <div v-else class="photo-circle photo-circle--loaded">
                          <img
                            :src="photoPreview"
                            alt="Student photo"
                            class="photo-circle-img"
                            referrerpolicy="no-referrer"
                            @click="triggerPhotoInput"
                          />
                          <b-button
                            variant="danger"
                            class="photo-delete-btn btn-icon rounded-circle"
                            size="sm"
                            @click.stop="clearPhoto"
                          >
                            <feather-icon icon="Trash2Icon" size="14" />
                          </b-button>
                        </div>
                        <input
                          ref="photoInput"
                          type="file"
                          accept="image/*"
                          hidden
                          @change="onPhotoSelected"
                        />
                      </b-media>
                      <h6 class="mb-0 mt-1 text-center text-muted">
                        {{ photoUploading ? "Uploading..." : "Upload photo" }}
                      </h6>
                    </b-form-group>
                  </b-col>
                </b-row>

                <div class="form-section">
                  <b-form-group
                    label-class="font-weight-bold"
                    class="field-spaced"
                    :state="groupState('convocationYear')"
                    invalid-feedback="Select class year (2024 or 2025)."
                  >
                    <template #label>
                      <inquiry-required-label text="Class of" :required="true" />
                    </template>
                    <div class="d-flex flex-wrap choice-inline">
                      <b-form-radio
                        v-for="year in displayYears"
                        :key="'year-' + year"
                        v-model="form.convocationYear"
                        name="convocation-class-year"
                        :value="year"
                        class="choice-check mr-3"
                        @change="clearValidationHint"
                      >
                        {{ year }}
                      </b-form-radio>
                    </div>
                  </b-form-group>

                  <b-form-group
                    label-class="font-weight-bold"
                    class="field-spaced mb-0"
                    :state="groupState('departmentName')"
                    invalid-feedback="Select a department."
                  >
                    <template #label>
                      <inquiry-required-label text="Department" :required="true" />
                    </template>
                    <b-row class="choice-grid">
                      <b-col
                        v-for="dept in displayDepartments"
                        :key="'dept-' + dept"
                        cols="12"
                        sm="6"
                        lg="3"
                      >
                        <b-form-radio
                          v-model="form.departmentName"
                          name="convocation-department"
                          :value="dept"
                          class="choice-radio"
                          @change="clearValidationHint"
                        >
                          {{ dept }}
                        </b-form-radio>
                      </b-col>
                    </b-row>
                  </b-form-group>
                </div>

                <hr class="section-divider" />
                <h6 class="section-title">Personal information</h6>

                <b-row class="form-fields-row">
                  <b-col cols="12" md="6" lg="4">
                    <b-form-group
                      label-class="font-weight-bold"
                      class="field-spaced"
                      :state="groupState('firstName')"
                      invalid-feedback="First name is required."
                    >
                      <template #label>
                        <inquiry-required-label text="First name" :required="true" />
                      </template>
                      <b-form-input
                        v-model="form.firstName"
                        placeholder="Enter first name"
                        @input="clearValidationHint"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="4">
                    <b-form-group
                      label-class="font-weight-bold"
                      class="field-spaced"
                      :state="groupState('lastName')"
                      invalid-feedback="Last name is required."
                    >
                      <template #label>
                        <inquiry-required-label text="Last name" :required="true" />
                      </template>
                      <b-form-input
                        v-model="form.lastName"
                        placeholder="Enter last name"
                        @input="clearValidationHint"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="4">
                    <b-form-group
                      label-class="font-weight-bold"
                      class="field-spaced"
                      :state="groupState('cnic')"
                      invalid-feedback="CNIC is required."
                    >
                      <template #label>
                        <inquiry-required-label text="Student's CNIC" :required="true" />
                      </template>
                      <b-form-input
                        v-model="form.cnic"
                        placeholder="Enter CNIC"
                        @input="clearValidationHint"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="4">
                    <b-form-group label="KSA Reg #" label-class="font-weight-bold" class="field-spaced">
                      <b-form-input v-model="form.regNo" placeholder="Enter Reg #" />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="4">
                    <b-form-group label="Date of birth" label-class="font-weight-bold" class="field-spaced">
                      <flat-pickr
                        v-model="form.dob"
                        :config="dobPickerConfig"
                        class="form-control"
                        placeholder="Select date of birth"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="4">
                    <b-form-group label="Gender" label-class="font-weight-bold" class="field-spaced">
                      <b-form-select v-model="form.gender" :options="genderOptions" />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12">
                    <b-form-group label="Address line 1" label-class="font-weight-bold" class="field-spaced">
                      <b-form-input
                        v-model="form.addressLine1"
                        placeholder="Enter address line 1"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12">
                    <b-form-group label="Address line 2" label-class="font-weight-bold" class="field-spaced">
                      <b-form-input
                        v-model="form.addressLine2"
                        placeholder="Enter address line 2"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="4">
                    <b-form-group label="Tel" label-class="font-weight-bold" class="field-spaced">
                      <b-form-input v-model="form.tel" placeholder="Enter home Tel" />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="4">
                    <b-form-group
                      label-class="font-weight-bold"
                      class="field-spaced"
                      :state="groupState('cellPhone')"
                      invalid-feedback="Cell / WhatsApp is required."
                    >
                      <template #label>
                        <inquiry-required-label text="Cell / WhatsApp" :required="true" />
                      </template>
                      <b-form-input
                        v-model="form.cellPhone"
                        placeholder="Enter cell #"
                        @input="clearValidationHint"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="4">
                    <b-form-group
                      label-class="font-weight-bold"
                      class="field-spaced"
                      :state="groupState('email')"
                      invalid-feedback="Enter a valid email."
                    >
                      <template #label>
                        <inquiry-required-label text="Email" :required="true" />
                      </template>
                      <b-form-input
                        v-model="form.email"
                        type="email"
                        placeholder="Enter email"
                        @input="clearValidationHint"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>

                <hr class="section-divider" />
                <h6 class="section-title">Guest details</h6>
                <p class="text-muted small guest-hint mb-2">
                  Optional — add names for guests attending with you.
                </p>
                <b-row
                  v-for="(g, idx) in form.guests.slice(0, 2)"
                  :key="'g1-' + idx"
                  class="guest-row align-items-md-center"
                >
                  <b-col cols="12" md="2" class="guest-label-col">
                    <span class="font-weight-bold">Guest {{ idx + 1 }}</span>
                  </b-col>
                  <b-col cols="12" md="5" class="guest-field-col">
                    <b-form-input v-model="g.name" placeholder="Name" />
                  </b-col>
                  <b-col cols="12" md="5" class="guest-field-col">
                    <b-form-input v-model="g.relationship" placeholder="Relationship" />
                  </b-col>
                </b-row>

                <h6 class="section-subtitle mt-3 mb-2">Additional guests</h6>
                <b-row
                  v-for="(g, idx) in form.guests.slice(2, 5)"
                  :key="'g2-' + idx"
                  class="guest-row align-items-md-center"
                >
                  <b-col cols="12" md="2" class="guest-label-col">
                    <span class="font-weight-bold">Guest {{ idx + 3 }}</span>
                  </b-col>
                  <b-col cols="12" md="5" class="guest-field-col">
                    <b-form-input v-model="g.name" placeholder="Name" />
                  </b-col>
                  <b-col cols="12" md="5" class="guest-field-col">
                    <b-form-input v-model="g.relationship" placeholder="Relationship" />
                  </b-col>
                </b-row>

                <b-row align-h="end" class="submit-row mt-3 pt-1">
                  <b-col cols="12" md="4" lg="3">
                    <b-button
                      type="submit"
                      variant="primary"
                      block
                      :disabled="submitting"
                    >
                      <b-spinner v-if="submitting" small class="mr-50" />
                      Submit
                    </b-button>
                  </b-col>
                </b-row>
              </b-form>
            </b-card>
          </b-col>
        </b-row>
      </template>
    </template>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BButton,
  BSpinner,
  BForm,
  BFormGroup,
  BFormInput,
  BFormCheckbox,
  BFormRadio,
  BFormSelect,
  BImg,
  BMedia,
} from "bootstrap-vue";
import { mapActions } from "vuex";
import { getUploadedPaths, uploadToMySkool } from "@/utils/gdriveUpload";
import { InquiryRequiredLabel } from "@/utils/inquiryRequiredLabel";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";

const SETTING_KEY = "convocation_form";
const PUBLIC_DEMO_TOKEN =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJkZW1vQG15c2tvb2wuYXBwIiwianRpIjoiYTdjYzU5YTEtZTRhOC00MmM1LWEzMmYtY2EzODM4ZTc0MDU4IiwiZXhwIjoyMDMwNjkyODMyLCJpc3MiOiJteXNrb29sLmFwcCIsImF1ZCI6Im15c2tvb2wuYXBwIn0.xuRNNjiKeO7158I-_Yni-rQUcl__LK6_oOtOwo9XfGs";

const DEFAULT_DEPARTMENTS = [
  "Fine Arts",
  "Communication Design",
  "Textile Design",
  "Digital Media",
];

const DEFAULT_YEARS = [2024, 2025];

function emptyGuests() {
  return Array.from({ length: 5 }, () => ({ name: "", relationship: "" }));
}

function emptyForm(campusID) {
  return {
    id: 0,
    campusID,
    departmentName: "",
    convocationYear: null,
    firstName: "",
    lastName: "",
    cnic: "",
    regNo: "",
    dob: "",
    gender: "",
    addressLine1: "",
    addressLine2: "",
    tel: "",
    cellPhone: "",
    email: "",
    photoUrl: "",
    guests: emptyGuests(),
  };
}

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BSpinner,
    BForm,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BFormRadio,
    BFormSelect,
    BImg,
    BMedia,
    InquiryRequiredLabel,
    flatPickr,
  },
  data() {
    return {
      dobPickerConfig: {
        altFormat: "j M, Y",
        altInput: true,
        dateFormat: "Y-m-d",
      },
      db: "",
      cId: 0,
      token: PUBLIC_DEMO_TOKEN,
      showError: false,
      pageLoading: true,
      submitted: false,
      submitting: false,
      photoUploading: false,
      photoPreview: "",
      formOptions: {
        years: [],
        departmentNames: [],
      },
      school: { logo: "", name: "", address: "", phone: "" },
      genderOptions: [
        { value: "", text: "Select gender" },
        { value: "Male", text: "Male" },
        { value: "Female", text: "Female" },
        { value: "Other", text: "Other" },
      ],
      form: emptyForm(0),
      showValidation: false,
    };
  },
  computed: {
    imgUrl() {
      const dark = this.$store.state.appConfig?.layout?.skin === "dark";
      return require(dark
        ? "@/assets/images/pages/error-dark.svg"
        : "@/assets/images/pages/error.svg");
    },
    displayYears() {
      const fromSettings = Array.isArray(this.formOptions.years)
        ? this.formOptions.years.map((y) => Number(y)).filter(Boolean)
        : [];
      const merged = [...new Set([...DEFAULT_YEARS, ...fromSettings])].sort(
        (a, b) => a - b
      );
      return merged.length ? merged : [...DEFAULT_YEARS];
    },
    displayDepartments() {
      const list = this.formOptions.departmentNames;
      if (Array.isArray(list) && list.length) return list;
      return DEFAULT_DEPARTMENTS;
    },
  },
  async created() {
    const q = this.$route.query;
    const cIdRaw = q.cId ?? q.cID;
    if (!q.db || !cIdRaw) {
      this.showError = true;
      this.pageLoading = false;
      return;
    }
    const cId = parseInt(cIdRaw, 10);
    if (!cId) {
      this.showError = true;
      this.pageLoading = false;
      return;
    }
    this.db = q.db;
    this.cId = cId;
    this.form = emptyForm(cId);
    await Promise.all([this.loadSchool(), this.loadFormOptions()]);
    this.pageLoading = false;
  },
  methods: {
    ...mapActions({ get: "get", post: "post" }),

    resetForAnother() {
      this.submitted = false;
      this.showValidation = false;
      this.clearPhoto();
      this.form = emptyForm(this.cId);
    },

    clearValidationHint() {
      if (this.showValidation) this.showValidation = false;
    },

    isValidEmail(val) {
      const s = String(val || "").trim();
      if (!s) return false;
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
    },

    fieldHasError(key) {
      switch (key) {
        case "convocationYear":
          return (
            this.form.convocationYear === null ||
            this.form.convocationYear === "" ||
            Number.isNaN(Number(this.form.convocationYear))
          );
        case "departmentName":
          return !String(this.form.departmentName || "").trim();
        case "firstName":
          return !String(this.form.firstName || "").trim();
        case "lastName":
          return !String(this.form.lastName || "").trim();
        case "cnic":
          return !String(this.form.cnic || "").trim();
        case "cellPhone":
          return !String(this.form.cellPhone || "").trim();
        case "email":
          return !this.isValidEmail(this.form.email);
        default:
          return false;
      }
    },

    groupState(key) {
      if (!this.showValidation) return null;
      return this.fieldHasError(key) ? false : true;
    },

    triggerPhotoInput() {
      if (!this.photoUploading) this.$refs.photoInput?.click();
    },

    clearPhoto() {
      this.photoPreview = "";
      this.form.photoUrl = "";
      if (this.$refs.photoInput) this.$refs.photoInput.value = "";
    },

    async onPhotoSelected(event) {
      const files = event.target?.files;
      if (!files?.length) return;
      this.photoUploading = true;
      try {
        const uploaded = await uploadToMySkool(files);
        const paths = getUploadedPaths(uploaded);
        if (paths.length) {
          this.form.photoUrl = paths[0];
          this.photoPreview = paths[0];
        }
      } catch (e) {
        console.error(e);
        this.$bvToast.toast("Photo upload failed. Try again.", {
          title: "Upload",
          variant: "warning",
          solid: true,
        });
      } finally {
        this.photoUploading = false;
        if (this.$refs.photoInput) this.$refs.photoInput.value = "";
      }
    },

    async loadSchool() {
      try {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "Campuses/GetSelected?id=" +
            this.cId +
            "&db=" +
            this.db,
          token: this.token,
        });
        if (res) {
          this.school = {
            logo: res.logo || "",
            name: res.branch || res.name || "",
            address: res.address || "",
            phone: res.phone || res.mobile || "",
          };
        }
      } catch (e) {
        console.error(e);
      }
    },

    async loadFormOptions() {
      try {
        const res = await this.post({
          url:
            this.$store.state.domain +
            "Settings/getSettings?db=" +
            this.db +
            "&cID=" +
            this.cId,
          body: [SETTING_KEY],
          token: this.token,
          subjects: true,
        });
        const row = Array.isArray(res)
          ? res.find((el) => el.key === SETTING_KEY)
          : null;
        if (row?.value) {
          const payload = JSON.parse(row.value);
          this.formOptions.years = Array.isArray(payload.years)
            ? payload.years
            : [];
          this.formOptions.departmentNames = Array.isArray(
            payload.departmentNames
          )
            ? payload.departmentNames
            : [];
        }
      } catch (e) {
        console.error(e);
      }
    },

    validateForm() {
      this.showValidation = true;
      const requiredKeys = [
        "convocationYear",
        "departmentName",
        "firstName",
        "lastName",
        "cnic",
        "cellPhone",
        "email",
      ];
      const invalid = requiredKeys.filter((k) => this.fieldHasError(k));
      if (invalid.length) {
        this.$bvToast.toast("Please fill all required fields (*).", {
          title: "Validation",
          variant: "warning",
          solid: true,
        });
        return false;
      }
      this.showValidation = false;
      return true;
    },

    buildSavePayload() {
      return {
        id: 0,
        campusID: Number(this.cId) || 0,
        departmentName: String(this.form.departmentName || "").trim(),
        convocationYear: String(this.form.convocationYear ?? ""),
        firstName: this.form.firstName,
        lastName: this.form.lastName,
        cnic: this.form.cnic || "",
        regNo: this.form.regNo || "",
        dob: this.form.dob || "",
        gender: this.form.gender || "",
        addressLine1: this.form.addressLine1 || "",
        addressLine2: this.form.addressLine2 || "",
        tel: this.form.tel || "",
        cellPhone: this.form.cellPhone || "",
        email: this.form.email || "",
        photoUrl: this.form.photoUrl || "",
        guests: this.form.guests.map((g) => ({
          name: g.name || "",
          relationship: g.relationship || "",
        })),
      };
    },

    apiErrorMessage(err) {
      const data = err?.response?.data;
      if (data?.errors && typeof data.errors === "object") {
        const msgs = Object.values(data.errors).flat().filter(Boolean);
        if (msgs.length) return msgs.join(" ");
      }
      if (data?.title) return String(data.title);
      return err?.message || "Submit failed. Please try again.";
    },

    async submitForm() {
      if (!this.validateForm()) return;
      this.submitting = true;
      try {
        const ok = await this.post({
          url:
            this.$store.state.domain +
            "ConvocationRegistration/Save?db=" +
            this.db +
            "&campusID=" +
            this.cId,
          body: this.buildSavePayload(),
          token: this.token,
          context: this,
        });
        if (ok === false) return;

        const firstName = String(this.form.firstName || "").trim();
        await this.$swal.fire({
          title: "Thank you!",
          html: `
            <p class="mb-1">Your convocation registration has been submitted successfully.</p>
            <p class="text-muted mb-0">We have received your details${
              firstName ? `, <strong>${firstName}</strong>` : ""
            }. You may close this page or register another entry.</p>
          `,
          icon: "success",
          confirmButtonText: "OK",
          customClass: {
            confirmButton: "btn btn-primary px-3",
          },
          buttonsStyling: false,
        });
        this.submitted = true;
      } catch (e) {
        console.error(e);
        this.$bvToast.toast(this.apiErrorMessage(e), {
          title: "Could not submit",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.convocation-public ::v-deep .convocation-form-card .card-body {
  padding: 1.5rem 1.25rem;
}

@media (min-width: 768px) {
  .convocation-public ::v-deep .convocation-form-card .card-body {
    padding: 1.75rem 2rem;
  }
}

.section-divider {
  margin: 1.25rem 0;
}

.section-title {
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: 0.8125rem;
  color: #5e5873;
  margin-bottom: 1rem;
}

.section-subtitle {
  font-weight: 600;
  font-size: 0.9rem;
  color: #6e6b7b;
}

.form-section {
  padding: 0.25rem 0 0.5rem;
}

.field-spaced {
  margin-bottom: 1.25rem;
}

.form-fields-row > [class*="col-"] {
  padding-bottom: 0.15rem;
}

.choice-inline {
  gap: 0.75rem 1.5rem;
  padding: 0.35rem 0 0.15rem;
}

.choice-check,
.choice-radio {
  margin-bottom: 0.65rem;
}

.choice-grid .col {
  padding-top: 0.2rem;
  padding-bottom: 0.35rem;
}

.guest-row {
  margin-bottom: 1rem;
}

.guest-label-col {
  margin-bottom: 0.35rem;
  padding-top: 0.25rem;
}

@media (min-width: 768px) {
  .guest-label-col {
    margin-bottom: 0;
  }

  .guest-field-col {
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }
}

.guest-hint {
  margin-top: -0.35rem;
}

.photo-row {
  padding-bottom: 0.5rem;
}

.submit-row {
  border-top: 1px solid #ebe9f1;
}

.convocation-form-card {
  transition: 0.4s;
}

.convocation-form-card:hover {
  box-shadow: 0 8px 16px 0 rgba(0, 0, 0, 0.2);
}

.photo-circle {
  position: relative;
  width: 120px;
  height: 120px;
  margin: 0 auto;
}

.photo-circle--empty {
  cursor: pointer;
}

.photo-circle-img {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #ebebeb;
  cursor: pointer;
}

.photo-delete-btn {
  position: absolute;
  top: 0;
  right: 0;
  padding: 0.35rem;
}
</style>
